"use client";

import { useUser } from "@clerk/tanstack-react-start";
import {
  UNIT_KINDS,
  isTransport,
  type PlayerId,
  type UnitKind,
  unitDef,
} from "@dailywars/core/engine";
import { UNIT_COLORS } from "@dailywars/core/render/constants";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis } from "recharts";

import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "#/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "#/components/ui/chart";
import { Field, FieldLabel } from "#/components/ui/field";
import { Switch } from "#/components/ui/switch";
import { playerProfileQueryOptions } from "#/lib/queries";
import type { GlobalStatsTotals } from "#/server/game/globalStats";

import { UnitAxisTick } from "./-stats.unitAxisTick";

const unitChartConfig = {
  globalPercentage: {
    label: "All players",
    color: "var(--chart-1)",
  },
  playerPercentage: {
    label: "You",
    color: "var(--accent)",
  },
} satisfies ChartConfig;
type UnitMetric = "built" | "killed";
type UnitStatsRecord = GlobalStatsTotals["unitsBuiltByKind"];
type UnitChartData = Array<{
  kind: UnitKind;
  label: string;
  globalCount: number;
  globalPercentage: number;
  globalPercentageLabel: string;
  playerCount?: number;
  playerPercentage?: number;
  playerPercentageLabel?: string;
  fill: string;
}>;
export function UnitStatsChart({
  title,
  record,
  metric,
  player,
  hideTransports = false,
}: {
  title: string;
  record: UnitStatsRecord;
  metric: UnitMetric;
  player: PlayerId;
  hideTransports?: boolean;
}) {
  const { user } = useUser();
  const [showYours, setShowYours] = useState(false);
  const switchId = useId();
  const playerQuery = useQuery({
    ...playerProfileQueryOptions(user?.id ?? ""),
    enabled: showYours && Boolean(user),
  });
  const unitKinds: readonly UnitKind[] = hideTransports
    ? UNIT_KINDS.filter((kind) => !isTransport(kind))
    : UNIT_KINDS;
  const playerRecord =
    showYours && playerQuery.data
      ? metric === "built"
        ? playerQuery.data.unitsBuilt
        : playerQuery.data.unitsKilled
      : undefined;
  const chartData = buildUnitChartData({ unitKinds, record, playerRecord, player });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{title} across all players.</CardDescription>
        {user ? (
          <CardAction>
            <Field orientation="horizontal" className="w-fit gap-2">
              <FieldLabel htmlFor={switchId}>Show yours</FieldLabel>
              <Switch id={switchId} size="sm" checked={showYours} onCheckedChange={setShowYours} />
            </Field>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent>
        <UnitBarChart data={chartData} player={player} />
      </CardContent>
    </Card>
  );
}
function buildUnitChartData({
  unitKinds,
  record,
  playerRecord,
  player,
}: {
  unitKinds: readonly UnitKind[];
  record: UnitStatsRecord;
  playerRecord?: UnitStatsRecord;
  player: PlayerId;
}): UnitChartData {
  const globalTotal = unitKinds.reduce((sum, kind) => sum + (record[kind] ?? 0), 0);
  const playerTotal = playerRecord
    ? unitKinds.reduce((sum, kind) => sum + (playerRecord[kind] ?? 0), 0)
    : 0;

  return unitKinds
    .map((kind) => {
      const globalPercentage = percentageOfTotal(record[kind] ?? 0, globalTotal);
      const playerPercentage = playerRecord
        ? percentageOfTotal(playerRecord[kind] ?? 0, playerTotal)
        : undefined;

      return {
        kind,
        label: unitDef(kind).name,
        globalCount: record[kind] ?? 0,
        globalPercentage,
        globalPercentageLabel: formatPercentage(globalPercentage),
        ...(playerPercentage === undefined
          ? {}
          : {
              playerCount: playerRecord?.[kind] ?? 0,
              playerPercentage,
              playerPercentageLabel: formatPercentage(playerPercentage),
            }),
        fill: UNIT_COLORS[player][kind],
      };
    })
    .sort((a, b) => b.globalPercentage - a.globalPercentage);
}
function percentageOfTotal(count: number, total: number): number {
  return total === 0 ? 0 : (count / total) * 100;
}

function formatPercentage(percentage: number | string): string {
  const value = Number(percentage);
  if (value === 0) return "0%";
  if (value < 1) return "<1%";
  return `${Math.round(value)}%`;
}
function formatUnitTooltipValue(
  value: number | string,
  item: { dataKey?: string | number; payload?: Record<string, unknown> },
): string {
  const countKey = item.dataKey === "playerPercentage" ? "playerCount" : "globalCount";
  const count = item.payload?.[countKey];
  const countLabel = typeof count === "number" ? count.toLocaleString() : "0";
  return `${countLabel} (${formatPercentage(value)})`;
}

function UnitBarChart({ data, player }: { data: UnitChartData; player: PlayerId }) {
  const showYours = data.some((entry) => entry.playerPercentage !== undefined);

  return (
    <ChartContainer config={unitChartConfig} className="h-224 w-full">
      <BarChart
        accessibilityLayer={false}
        data={data}
        layout="vertical"
        margin={{ right: 48 }}
        barSize={showYours ? 20 : undefined}
      >
        <CartesianGrid horizontal={false} />
        <XAxis
          type="number"
          tickLine={false}
          axisLine={false}
          tickFormatter={formatPercentage}
          tickCount={6}
        />
        <YAxis
          dataKey="kind"
          type="category"
          tickLine={false}
          axisLine={false}
          width={64}
          tick={({ x, y, payload }) => (
            <UnitAxisTick kind={payload.value as UnitKind} player={player} x={x} y={y} />
          )}
        />
        <ChartTooltip
          content={<ChartTooltipContent valueFormatter={formatUnitTooltipValue} />}
          cursor={false}
          shared={false}
        />
        <UnitBarSeries data={data} showYours={showYours} />
      </BarChart>
    </ChartContainer>
  );
}

function UnitBarSeries({ data, showYours }: { data: UnitChartData; showYours: boolean }) {
  return (
    <>
      <Bar dataKey="globalPercentage" radius={4}>
        <LabelList
          dataKey="globalPercentageLabel"
          position="right"
          offset={8}
          className="fill-foreground"
          fontSize={12}
        />
        {data.map((entry) => (
          <Cell key={entry.kind} fill={entry.fill} />
        ))}
      </Bar>
      {showYours ? (
        <Bar dataKey="playerPercentage" fill="var(--color-playerPercentage)" radius={4}>
          <LabelList
            dataKey="playerPercentageLabel"
            position="right"
            offset={8}
            className="fill-foreground"
            fontSize={12}
          />
          {data.map((entry) => (
            <Cell key={entry.kind} fill="var(--color-playerPercentage)" />
          ))}
        </Bar>
      ) : null}
    </>
  );
}
